import React from "react";
import PublicLegalLayout from "./PublicLegalLayout";
import {
  TERMS_OF_USE_SECTIONS,
  TERMS_COPY,
  PAYMENTS_OVERVIEW,
  PAYMENTS_PROCESSING,
  PRICING_PLANS,
  SUPPORT_EMAIL,
  SUPPORT_MAILTO,
  LAST_UPDATED,
} from "../../lib/legalCopy";

/**
 * Public Terms of Use page. Renders the full industry-grade Terms of Use
 * sections (acceptance, eligibility, accuracy disclaimers, vault disclaimer,
 * AI disclaimer, limitation of liability, indemnification, arbitration +
 * class-action waiver, etc.) plus the existing payments / pricing /
 * cancellation surfaces that Stripe and Apple expect to see on the
 * merchant's website. Same content is mirrored in the in-app
 * Settings → Terms sub-page (see `SettingsTab.jsx`).
 */
export default function TermsPage() {
  // Split the cancellation copy so the support email becomes a real mailto.
  const [termsPre, termsPost] = TERMS_COPY.split(SUPPORT_EMAIL);

  return (
    <PublicLegalLayout title="Terms of Use">
      <p className="text-navy-soft text-xs uppercase tracking-widest font-jakarta font-bold">
        Last updated: {LAST_UPDATED}
      </p>

      {/* Lead: full industry-grade Terms of Use sections. */}
      {TERMS_OF_USE_SECTIONS.map((section) => (
        <section
          key={section.id}
          id={`terms-${section.id}`}
          data-testid={`terms-section-${section.id}`}
          className="bg-white rounded-3xl shadow-soft border border-navy/5 p-6 sm:p-7"
        >
          <h2 className="font-jakarta font-extrabold text-xl text-navy mb-3">
            {section.heading}
          </h2>
          <p className="whitespace-pre-line">{section.body}</p>
        </section>
      ))}

      {/* Detailed payments / pricing / cancellation appendix retained for
          Stripe and Apple reviewers (commercial disclosure expectations). */}
      <section
        data-testid="terms-payments-overview"
        className="bg-white rounded-3xl shadow-soft border border-navy/5 p-6 sm:p-7"
      >
        <h2 className="font-jakarta font-extrabold text-xl text-navy mb-3">
          Appendix A — Payments &amp; Subscriptions
        </h2>
        <p className="mb-3">{PAYMENTS_OVERVIEW}</p>
        <p>{PAYMENTS_PROCESSING}</p>
      </section>

      <section
        data-testid="terms-pricing"
        className="bg-white rounded-3xl shadow-soft border border-navy/5 p-6 sm:p-7"
      >
        <h2 className="font-jakarta font-extrabold text-xl text-navy mb-3">
          Appendix B — Current Plans
        </h2>
        <ul className="divide-y divide-navy/10">
          {PRICING_PLANS.map((p) => (
            <li
              key={p.label}
              className="py-3 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1"
            >
              <span className="font-jakarta font-bold text-navy">{p.label}</span>
              <span className="font-jakarta text-navy-soft text-sm">{p.price}</span>
            </li>
          ))}
        </ul>
      </section>

      <section
        data-testid="terms-cancellation"
        className="bg-white rounded-3xl shadow-soft border border-navy/5 p-6 sm:p-7"
      >
        <h2 className="font-jakarta font-extrabold text-xl text-navy mb-3">
          Appendix C — Cancellation &amp; Refunds
        </h2>
        <p>
          {termsPre}
          <a
            href={SUPPORT_MAILTO}
            data-testid="terms-support-mailto"
            className="text-orange font-bold hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
          {termsPost}
        </p>
      </section>
    </PublicLegalLayout>
  );
}
